import React from 'react';
import { View, StyleSheet } from 'react-native';
import { usePathname } from 'expo-router';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { isMapRoute } from '@utils/sidebarVariant';
import { SidebarNav } from './SidebarNav';

interface AppShellProps {
  children: React.ReactNode;
  /** Suppress the sidebar (auth/onboarding flows render full-bleed). */
  chromeless?: boolean;
  /**
   * The map-tools rail's four pinned rows (Layers, Key, Present, Plan —
   * MapRailTools.web.tsx). A ReactNode, not an items array, so its store
   * subscriptions live in that component and a tool toggling doesn't
   * re-render this shell or SidebarNav. Only reaches SidebarNav in rail
   * mode — every other screen's sidebar has no map tools to show.
   */
  mapTools?: React.ReactNode;
}

/**
 * Desktop shell chrome. On the desktop web breakpoint it lays the persistent
 * sidebar beside the router's screen tree; everywhere else it renders
 * children untouched, so mobile/tablet/native are bit-identical with or
 * without this wrapper.
 *
 * The map screen's sidebar is always the 64px icon rail (Sept 2026 client
 * ask: the map is now the main workspace — people draw defense plans on it
 * with a group — so the full 240px sidebar cost too much of it;
 * SIDEBAR_RAIL_WIDTH in theme/layout.ts). Every other desktop screen keeps
 * the full sidebar. `isMapRoute` (src/utils/sidebarVariant.ts) is the pure
 * choice; there is no expand/collapse anymore — the rail is not a user
 * preference, it's what the map route always shows.
 *
 * The rail stays mounted while the defense plan editor/viewer is docked —
 * unlike the old behavior, which hid the sidebar entirely for that surface's
 * duration. `DockedPanel`'s `left: 0` (side="left") resolves inside this
 * component's own `content` View, which already starts to the right of
 * whatever width the sidebar/rail currently takes, so the docked panel and
 * the sidebar never overlap; no coordinate math needed on top of what
 * `content`'s flex row already gives for free. `children` (the router's
 * screen tree, the map included) stays mounted the whole time either way, so
 * Leaflet is never remounted by a variant change — Map.web.tsx's own
 * ResizeObserver picks up the container-width change and calls
 * invalidateSize.
 *
 * The sidebar footer is the desktop shell's only sign-in/sign-up affordance
 * and its only path to the other tabs (MapSignInOverlay renders nothing on
 * the desktop shell) — this is why it must never fully hide, on the map
 * screen or anywhere else. The rail keeps that affordance too, just moved to
 * the top of the identity block instead of the footer (see SidebarNav).
 */
export function AppShell({ children, chromeless = false, mapTools }: AppShellProps) {
  const isDesktopShell = useDesktopShell();
  const pathname = usePathname();

  if (!isDesktopShell || chromeless) {
    return <>{children}</>;
  }

  const variant = isMapRoute(pathname) ? 'rail' : 'full';

  return (
    <View style={styles.row}>
      <SidebarNav variant={variant} mapTools={variant === 'rail' ? mapTools : undefined} />
      <View style={styles.content}>{children}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flex: 1,
    flexDirection: 'row',
  },
  content: {
    flex: 1,
  },
});
